import { HeadFC, graphql, PageProps, withPrefix } from "gatsby"
import { FC, useEffect, useMemo, useState } from "react"
import { useContentfulInspectorMode } from "@contentful/live-preview/react"
import { useDispatch, useSelector } from "react-redux"

import Joyride, { CallBackProps, STATUS } from "react-joyride"
import {
  JoyrideTourGuide,
  JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT
} from "@eventbrite/marmalade"
// @ts-ignore
import * as tourGuideStyles from "@eventbrite/marmalade/lib/components/TourGuide/TourGuide.css"

// components
import { CanonicalTag } from "../../components/CanonicalTag/CanonicalTag"
import { HeadUniversal } from "../../components/HeadUniversal/HeadUniversal"
import { HrefLangTags } from "../../components/HrefLangTags/HrefLangTags"
import { Layout } from "../../components/Layout/Layout"
import { SearchBar } from "../../components/HeaderSiteSearch/SearchBar/SearchBar"
import { Tabs } from "../../components/EDS/Tabs/Tabs"

// data
import { staticHrefLangTags } from "../../utils/seo/href_lang_tag_data"

// types
import { RootState } from "../../store/store"

// utils
import {
  generateDescriptionMetaTag,
  generateRobotsMetaTag,
  getTitleText
} from "../../utils/seo/seo_utils"
import { events } from "../../utils/heap/tracking_events"
import { assignLangCode } from "../../utils/general_utils"
import { userProfileGuardCheck } from "../../store/slices/user_profile.slice"

// styles
import {
  screenReaderText,
  searchContainer,
  searchTitle,
  searchInputContainer,
  searchForm,
  homepageBackgroundImageContainer,
  tabsWrapper
} from "./index.module.scss"
import { MicroCopy } from "../../components/ContactForm/ContactForm"
import {
  customRendererToRemovePTag,
  renderRichText
} from "../../utils/contentful/render_rich_text"

export type PageContext = {
  id: string
  node_locale: string
}

const HomePage: FC<PageProps<Queries.HomepageDataQuery, PageContext>> = (
  pageProps
) => {
  const data = pageProps?.data
  const inspectorProps = useContentfulInspectorMode({
    entryId: data?.helpCenterHomePage?.contentful_id
  })
  const prefixedBasePath = withPrefix(
    `/${pageProps?.pageContext["node_locale"]?.toLowerCase()}`
  )
  const currentLangCode = useSelector(
    (state: RootState) => state.metaData.langCode
  )

  const userProfile = userProfileGuardCheck(
    useSelector((state: RootState) => state.userProfile)
  )
  const isOrganizer = userProfile?.isOrganizer
  const [runTour, setRunTour] = useState(false)

  const LS_KEY = "organizerTabSeen"

  useEffect(() => {
    if (!isOrganizer) {
      setRunTour(false)
      return
    }

    const seen = localStorage.getItem(LS_KEY) === "1"
    if (seen) {
      setRunTour(false)
      return
    }

    const target = document.querySelector('[data-tour-step="organizer-tab"]')
    setRunTour(Boolean(target))
  }, [isOrganizer])

  const onJoyride = ({ status }: CallBackProps) => {
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      localStorage.setItem(LS_KEY, "1")
      setRunTour(false)
    }
  }

  const organizerToolTipMicrocopy = useMemo(() => {
    const objMap = data?.organizerTabTooltip?.microcopySetEntries?.map(
      (entry) => Object.values(entry as { [key: string]: MicroCopy })
    )
    return Object.fromEntries(objMap as [string, MicroCopy][])
  }, [data?.organizerTabTooltip?.microcopySetEntries])

  const toolTipHeader = renderRichText(
    organizerToolTipMicrocopy?.headerTooltipOrganizingEvents?.raw,
    customRendererToRemovePTag
  )
  const toolTipContent = renderRichText(
    organizerToolTipMicrocopy?.messageTooltipOrganizerResources?.raw,
    customRendererToRemovePTag
  )

  const rawSteps = [
    {
      target: '[data-tour-step="organizer-tab"]',
      title: toolTipHeader ?? "Looks like you organize events",
      content: toolTipContent ?? "These resources are for you.",
      placement: "bottom" as const,
      placementBeacon: "right" as const,
      hidePrimary: true,
      styles: {
        beacon: { position: "absolute" as const, right: -6, top: -6 }
      }
    }
  ]
  const steps = rawSteps.map((s) => ({ ...s, totalSteps: rawSteps.length }))

  const styles = {
    ...tourGuideStyles,
    beaconInner: { backgroundColor: "#F05537" },
    beaconOuter: {
      borderColor: "#F05537"
    },
    buttonClose: { display: "none" },
    options: {
      beaconSize: 12
    }
  }

  const dispatch = useDispatch()

  useEffect(() => {
    const scheduleWork = window.requestIdleCallback || window.setTimeout
    scheduleWork(() => {
      assignLangCode(currentLangCode, dispatch)
    })
  }, [currentLangCode, dispatch])

  useEffect(() => {
    const isUserProfile = (
      p: typeof userProfile
    ): p is Exclude<typeof userProfile, Record<string, never>> =>
      "organizationId" in p
    if (userProfile && isUserProfile(userProfile)) {
      const scheduleWork = window.requestIdleCallback || window.setTimeout
      scheduleWork(() => {
        events?.userProfileData("User Profile", userProfile)
      })
    }
  }, [userProfile])

  return (
    <Layout
      gatsbyPageProps={pageProps}
      className={homepageBackgroundImageContainer}
      microCopy={{
        buttonBrowseHelpCenter: data?.buttonBrowseHelpCenter,
        buttonContactUs: data?.buttonContactUs,
        messageContactUs: data?.messageContactUs,
        upperNavMicroCopySet: data?.upperNavMicroCopySet,
        headerSearchResults: data?.headerSearchResults,
        messageSearchBar: data?.messageSearchBar,
        chatbotUITranslations: data?.chatbotUITranslations
      }}
      footerCopy={data?.contentfulFooter?.footerReference ?? null}
      footerNavCopy={
        data?.contentfulFooterNavigation?.FooterNavigationChildItems ?? null
      }
    >
      <div role="search" className={searchContainer}>
        <h1
          id="searchBarLabel"
          aria-label="searchBar"
          className={searchTitle}
          {...inspectorProps({ fieldId: "homepageTitle" })}
        >
          {data?.helpCenterHomePage?.title}
        </h1>
        <span className={screenReaderText}>Search help articles</span>
        <div className={searchInputContainer}>
          <SearchBar
            messageSearchBarMicrocopy={data?.messageSearchBar}
            wrapperClassName={searchForm}
            takeover={{
              enabled: false
            }}
          />
        </div>
      </div>

      {runTour && (
        <Joyride
          steps={steps}
          run={runTour}
          tooltipComponent={JoyrideTourGuide}
          styles={styles}
          floaterProps={{ styles: JOYRIDE_TOURGUIDE_FLOATER_STYLES_DEFAULT }}
          disableOverlay
          continuous={true}
          locale={{
            last: ""
          }}
          hideBackButton={true}
          callback={onJoyride}
        />
      )}
      <div className={tabsWrapper}>
        <Tabs
          prefixedBasePath={prefixedBasePath}
          attendee={data?.helpCenterHomePage?.attendee}
          organizer={data?.helpCenterHomePage?.organizer}
          dflt={data?.helpCenterHomePage?.default}
          noTicketsFoundActionComplex={data?.noTicketsFoundActionComplex}
          ticketsFoundActionComplex={data?.ticketsFoundActionComplex}
          messageNoUpcomingOrders={data?.messageNoUpcomingOrders}
          searchingForTickets={data?.searchingForTickets}
          noEventsFoundActionComplex={data?.noEventsFoundActionComplex}
          eventsFoundActionComplex={data?.eventsFoundActionComplex}
          searchingForEvents={data?.searchingForEvents}
          messageNoUpcomingEvents={data?.messageNoUpcomingEvents}
          loginButtonMicrocopy={data?.loginButtonMicrocopy}
          loginContentMicrocopy={data?.loginContentMicrocopy}
          loginHeaderMicrocopy={data?.loginHeaderMicrocopy}
          cantLogInMicrocopy={data?.cantLogInMicrocopy}
          cantLogInArticle={data?.cantLogInArticle}
        />
      </div>
    </Layout>
  )
}

export const Head: HeadFC<Queries.HomepageDataQuery, PageContext> = (
  headArgs
) => {
  const canonicalUrl = useMemo<string>(() => {
    if (headArgs?.location?.pathname?.length > 0) {
      return headArgs?.location?.pathname
    }
    return ""
  }, [headArgs?.location?.pathname])
  // should no output hreflang or canonical tags if page should be noindex
  const shouldNoIndex = useMemo(
    () =>
      !headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata
        ?.seoMetaTagRobotsIndex,
    [
      headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata
        ?.seoMetaTagRobotsIndex
    ]
  )

  const seoTitle =
    headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata?.seoMetaTagTitle
  const seoDescription =
    headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata
      ?.seoMetaTagDescription
  const robotsIndex =
    headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata
      ?.seoMetaTagRobotsIndex
  const crawlerFollow =
    headArgs?.data?.helpCenterHomePage?.landingPageSeoMetadata
      ?.seoMetaTagCrawlerFollow

  return (
    <>
      <HeadUniversal
        {...headArgs}
        metaTitle={seoTitle}
        metaDescription={seoDescription}
        contentType="website"
      />
      {getTitleText(seoTitle ?? "")}
      {generateDescriptionMetaTag(seoDescription ?? "")}
      {generateRobotsMetaTag(robotsIndex ?? false, crawlerFollow ?? false)}
      <HrefLangTags
        metaData={staticHrefLangTags("/")}
        shouldNoIndex={shouldNoIndex}
      />
      <CanonicalTag
        nodeLocale={headArgs?.pageContext?.node_locale}
        href={canonicalUrl}
        shouldNoIndex={shouldNoIndex}
      />
    </>
  )
}

export const query = graphql`
  query HomepageData($node_locale: String!) {
    # PERSONALIZATION
    # FIND MY TICKETS
    messageNoUpcomingOrders: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingOrders" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      node_locale
      microcopyValue {
        raw
      }
    }

    searchingForTickets: contentfulEventActionComplex(
      contentful_id: { eq: "HGMxLdiIS4NgziflHZXtU" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    ticketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "7qfRoL77Rw1J9AFa7IcKnv" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noTicketsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "4VjzGzF1EYJKdWthSHYRrH" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY TICKETS

    # FIND MY EVENTS
    messageNoUpcomingEvents: contentfulMicrocopy(
      microcopyKey: { eq: "messageNoUpcomingEvents" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      microcopyValue {
        raw
      }
    }

    searchingForEvents: contentfulEventActionComplex(
      contentful_id: { eq: "7dL10FjtyQxQglQ0RH1tos" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexLoadingMessage {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    noEventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "6DJb2f0Kj2xvTlsFsTx0Yd" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }

    eventsFoundActionComplex: contentfulEventActionComplex(
      contentful_id: { eq: "63OCAocrPDWrso7ye5VBdp" }
      node_locale: { eq: $node_locale }
    ) {
      id
      node_locale
      eventActionComplexInternalTitle
      eventActionComplexHeader
      eventActionComplexPrimaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexSecondaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexTertiaryCta {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
      eventActionComplexOtherOptions {
        id
        microcopyInternalTitle
        microcopyKey
        microcopyValue {
          raw
        }
      }
    }
    # END FIND MY EVENTS
    # END PERSONALIZATION

    # LOGIN
    loginContentMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "messageLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      node_locale
      microcopyInternalTitle
      microcopyKey
      microcopyValue {
        raw
      }
    }

    loginHeaderMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "headerLoginResources" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    loginButtonMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInMicrocopy: contentfulMicrocopy(
      microcopyKey: { eq: "navigationCantLogIn" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyValue {
        raw
      }
    }

    cantLogInArticle: contentfulHelpArticle(
      articleId: { eq: 221530 }
      node_locale: { eq: $node_locale }
    ) {
      articleId
      slug
    }

    # ORGANIZER TAB TOOL TIP
    organizerTabTooltip: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Tooltip for organizer help resources" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetInternalName
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }
    # END ORGANIZER TAB TOOL TIP

    chatbotUITranslations: allContentfulMicrocopySet(
      filter: {
        microcopySetInternalName: { eq: "Chatbot UI" }
        node_locale: { eq: $node_locale }
      }
    ) {
      edges {
        node {
          node_locale
          microcopySetEntries {
            ... on ContentfulMicrocopy {
              contentful_id
              microcopyKey
              microcopyValue {
                raw
              }
            }
          }
        }
      }
    }

    upperNavMicroCopySet: contentfulMicrocopySet(
      microcopySetInternalName: { eq: "Help Center nav" }
      node_locale: { eq: $node_locale }
    ) {
      microcopySetEntries {
        ... on ContentfulMicrocopy {
          microcopyKey
          microcopyValue {
            raw
          }
        }
      }
    }
    messageSearchBar: contentfulMicrocopy(
      microcopyKey: { eq: "messageSearchBar" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerSearchResults: contentfulMicrocopy(
      microcopyKey: { eq: "headerSearchResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    headerNoSearchTermEntered: contentfulMicrocopy(
      microcopyKey: { eq: "headerNoSearchTermEntered" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchAllResults: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchAllResults" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchAttendeeTopics: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchAttendeeTopics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    filterSearchOrganizerTopics: contentfulMicrocopy(
      microcopyKey: { eq: "filterSearchOrganizerTopics" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    navigationHomepage: contentfulMicrocopy(
      microcopyKey: { eq: "navigationHomepage" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "buttonContactUs" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    buttonBrowseHelpCenter: contentfulMicrocopy(
      microcopyKey: { eq: "buttonBrowseHelpCenter" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    messageContactUs: contentfulMicrocopy(
      microcopyKey: { eq: "messageStillHaveQuestions" }
      node_locale: { eq: $node_locale }
    ) {
      microcopyKey
      microcopyValue {
        raw
      }
    }
    contentfulFooter(node_locale: { eq: $node_locale }) {
      id
      footerInternalName
      footerReference {
        displayText
        FooterNavigationChildItems {
          footerNavigationInternalTitle
          displayText
          footerNavigationUrl
        }
      }
    }
    contentfulFooterNavigation(
      footerNavigationInternalTitle: { eq: "Category: Bottom row navigation" }
      node_locale: { eq: $node_locale }
    ) {
      footerNavigationInternalTitle
      footerNavigationUrl
      FooterNavigationChildItems {
        footerNavigationInternalTitle
        displayText
        footerNavigationUrl
      }
    }
    helpCenterHomePage: contentfulLandingPage(
      contentful_id: { eq: "7LCib6F4igPPYP0UoJIeA4" }
      node_locale: { eq: $node_locale }
    ) {
      contentful_id
      internalHomepageTitle
      title: homepageTitle
      attendee: landingPageAttendee {
        landingPageVariationInternalTitle
        title: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulFeaturedArticles {
            contentful_id
            title: featuredArticlesTitle
            attendeeArticles: featuredArticlesList {
              title: helpArticleTitle
              id
              slug
              articleId
            }
          }
          ... on ContentfulFeaturedCategories {
            title: featuredCategoriesTitle
            attendeeCategories: featuredCategoriesList {
              title: categoryTitle
              iconName: svgIconName
              slug: categoryMenuSlug
            }
          }
        }
      }
      organizer: landingPageOrganizer {
        landingPageVariationInternalTitle
        title: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulFeaturedArticles {
            contentful_id
            title: featuredArticlesTitle
            organizerArticles: featuredArticlesList {
              title: helpArticleTitle
              slug
              id
              articleId
            }
          }
          ... on ContentfulFeaturedCategories {
            title: featuredCategoriesTitle
            organizerCategories: featuredCategoriesList {
              slug: categoryMenuSlug
              title: categoryTitle
              iconName: svgIconName
            }
          }
        }
      }
      default: landingPageDefaultUser {
        title: landingPageVariationTitle
        content: landingPageVariationContent {
          ... on ContentfulFeaturedArticles {
            contentful_id
            title: featuredArticlesTitle
            attendeeArticles: featuredArticlesList {
              title: helpArticleTitle
              id
              slug
              articleId
            }
          }
          ... on ContentfulFeaturedCategories {
            title: featuredCategoriesTitle
            attendeeCategories: featuredCategoriesList {
              title: categoryTitle
              iconName: svgIconName
              slug: categoryMenuSlug
            }
          }
        }
      }
      landingPageSeoMetadata {
        seoMetaTagCrawlerFollow
        seoMetaTagDescription
        seoMetaTagRobotsIndex
        seoMetaTagTitle
      }
    }
  }
`

export default HomePage
